import React, { useEffect, useRef, useState } from 'react';
import './session.scss';

import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import fileDownload from 'js-file-download';
import { useIntl } from 'react-intl';
import {
  getJsonFromStorage,
  getPaginationOpts,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../../utils/utils';
import { downloadSession, loadSessionList } from '../actions';
import { getSystemData } from '../../System/reducer';
import Loader from '../../../components/base/Loader/Loader';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import { getSessionData } from '../reducer';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import {
  checkBoxFormatter,
  dateTimeFormatter, regNumberFormatter, tooltipFormatter, violationFormatter,
} from '../../../utils/formatters';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import SessionViewModal from './SessionViewModal';
import TextInput from '../../../components/fields/TextInput/TextInput';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import DatePicker from '../../../components/fields/DatePicker/DatePicker';
import Pagination from '../../../components/base/Pagination/Pagination';
import FilterForm from './FilterForm';
import CollageModal from './CollageModal';
import { getCamerasData } from '../../Settings/reducer';
import SessionFormDTO from '../../../dto/SessionFormDTO';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import { errorNotification } from '../../Notifications/notificationDecorator';
import DateTimePicker from '../../../components/fields/DatePicker/DateTimePicker';

export default function SessionPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [selected, setSelected] = useState([]);
  const [showSession, setShowSession] = useState(null);
  const [showCollage, setShowCollage] = useState(null);
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [filter, setFilter] = useState({});
  const { updatePeriod } = useSelector(getSystemData);
  const DTO = new SessionFormDTO();
  const attributes = DTO.getAttributesByName();
  const intl = useIntl();
  const { formatMessage } = intl;
  const {
    loadingSessionList,
    sessionList,
    sessionListMeta = {},
    downloadingSession,
  } = useSelector(getSessionData);

  const { cameraList = [] } = useSelector(getCamerasData);
  const defaultFilterValues = {
    find: '',
    date: '',
    date_start: '',
    date_end: '',
    type: '',
    reg_number: '',
    show_conditionally_recognized: false,
    car_type: '',
    camera: '',
    zone: '',
  };

  const {
    register, getValues, watch, setValue, reset, control,
  } = useForm({
    defaultValues: getJsonFromStorage('session-filter') || { ...defaultFilterValues },
  });
  watch(data => setFilter(data));
  const loadData = opts => {
    const paginationOpts = getPaginationOpts(sessionListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter,
      reverse_order: true,
      // sort: sortData.join(','),
    };
    const p1 = dispatch(loadSessionList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  useEffect(() => { loadData(); }, [filter]);

  const headerCheckBoxFormatter = (id, item) => {
    const isAll = sessionList.every(p => selected.includes(p.id));
    const isSome = sessionList.some(p => selected.includes(p.id)) && !isAll;
    const onChange = () => {
      if (isAll) {
        setSelected([]);
      } else {
        setSelected(sessionList.map(r => r.id));
      }
    };
    return checkBoxFormatter(isAll, onChange, classNames({ 'some-child': isSome }, { 'every-child': isAll }));
  };

  const rowCheckBoxFormatter = (_, item) => {
    const onChange = () => {
      const index = selected.indexOf(item.id);
      if (index >= 0) {
        const newSelected = [...selected];
        newSelected.splice(index, 1);
        setSelected(newSelected);
      } else {
        setSelected([...selected, item.id]);
      }
    };
    return checkBoxFormatter(selected.includes(item.id), onChange);
  };

  const cameraFormatter = id => {
    const camera = cameraList.find(f => f.id === id);
    return camera && camera.name ? camera.name : id;
  };

  const sessionListColumns = [
    {
      dataField: 'checkbox',
      text: formatMessage({ id: 'select.row' }),
      disableSort: true,
      defaultVisible: false,
      headerFormatter: headerCheckBoxFormatter,
      formatter: rowCheckBoxFormatter,
      dataClassName: 'checkbox-column',
      headerClassName: 'checkbox-column',
    },
    {
      dataField: 'start_time',
      text: formatMessage({ id: 'session.start.time' }),
      formatter: dateTimeFormatter,
      headerClassName: 'date-column',
      disableSort: true,
    },
    {
      dataField: 'end_time',
      text: formatMessage({ id: 'session.end' }),
      formatter: dateTimeFormatter,
      headerClassName: 'date-column',
      disableSort: true,
    },
    {
      dataField: 'lp_text',
      text: formatMessage({ id: 'reg_number' }),
      formatter: regNumberFormatter,
      headerClassName: 'reg-number-column',
      disableSort: true,
    },
    {
      dataField: 'camera',
      text: formatMessage({ id: 'camera' }),
      formatter: cameraFormatter,
      headerClassName: 'camera-column',
      disableSort: true,
    },
    {
      dataField: 'zone_name',
      text: formatMessage({ id: 'zone' }),
      formatter: tooltipFormatter,
      headerClassName: 'zone-column',
      disableSort: true,
    },
    {
      dataField: 'address',
      text: formatMessage({ id: 'address' }),
      formatter: tooltipFormatter,
      headerClassName: 'address-column',
      disableSort: true,
    },
    {
      dataField: 'violations',
      text: formatMessage({ id: 'session.violations' }),
      formatter: violationFormatter,
      headerClassName: 'violation-column',
      disableSort: true,
    },
    {
      dataField: 'id',
      text: formatMessage({ id: 'collage' }),
      formatter: (id, item) => {
        if (!item.violations || item.violations.length < 1) {
          return null;
        }
        return (
          <button
            type="button"
            className="collage-formatter"
            onClick={event => {
              event.stopPropagation();
              setShowCollage(id);
            }}
          >
            <Icons width={20} height={20} customIcon icon="image-icon" className="image-icon" /> {formatMessage({ id: 'collage' })}
          </button>
        );
      },
      headerClassName: 'collage-column',
      disableSort: true,
    },
  ];

  const sessionTypeList = [
    { value: 0, name: formatMessage({ id: 'session.type.0' }) },
    { value: 1, name: formatMessage({ id: 'session.type.1' }) },
  ];

  const downloadFile = () => {
    dispatch(downloadSession(selected))
      .then(({ data, timestamp }) => {
        if (data) {
          fileDownload(data, `sessions-${timestamp}.zip`);
        }
      })
      .catch(() => { errorNotification(formatMessage({ id: 'session.download' })); });
  };

  return (
    <div className="page session-page">
      <Loader loading={loadingSessionList || downloadingSession} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.session' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={TextInput}
            dtoObject={attributes.find}
            className="no-border"
            inputProps={{ ...register('find') }}
            inputBefore={(
              <div className="search-wrapper">
                <Icons customIcon className="search" width={24} height={24} icon="search" />
              </div>
            )}
          />
          <FieldDTO
            control={control}
            component={DateTimePicker}
            dtoObject={attributes.start_time}
            hideSmallButtons
            showArrowIcon
            usePortal
            className="no-border"
          />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={attributes.camera}
            hideSmallButtons
            showArrowIcon
            usePortal
            className="no-border"
            options={cameraList}
          />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={attributes.session_type}
            hideSmallButtons
            showArrowIcon
            usePortal
            className="no-border"
            options={sessionTypeList}
          />
          <div className="filter-sep" />
          <Button variant="second" className="" disabled={!selected.length} onClick={downloadFile}><Icons customIcon className="file-download" width={24} height={24} icon="download" /></Button>
          <div className="filter-sep" />
          <Button variant="second" className="" onClick={() => { setOpenFilter(true); }}><Icons customIcon className="file-download" width={24} height={24} icon="filter" /></Button>
          {isOpenFilter
            ? (
              <FilterForm
                control={control}
                onHide={() => { setOpenFilter(false); }}
                getValues={getValues}
                reset={reset}
                defaultValues={defaultFilterValues}
                meta={sessionListMeta}
              />
            )
            : null}
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid data={sessionList} columns={sessionListColumns} name="session-list-v2" />
              {/* <CustomizedGrid data={sessionList} columns={sessionListColumns} name="session-list" settingsFormatter={(_, item) => (<Icons onClick={() => setShowSession(item)} customIcon icon="dot" className="more-vert" />)} /> */}
              {showSession ? <SessionViewModal handleClose={() => setShowSession(null)} session={showSession} /> : null}
              {showCollage ? <CollageModal handleClose={() => setShowCollage(null)} session={showCollage} /> : null}
            </div>
          </div>

          <Pagination meta={sessionListMeta} list={sessionList} handleChangeMeta={loadData} />
        </div>
      </div>
    </div>
  );
}
